import { TOAST_TIMEOUT } from 'src/config/Toast';
import store from 'src/store';
import { createToast } from 'src/store/toastsSlice';
import apiClient from 'src/utils/apiClient';
import { SitePayload, SiteCapacityPayload, Site } from './types';
import {
  BuildingType,
  Country,
  Org,
  SubOrg,
  TableDataRow,
} from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { ApplicableJob, SiteCapacityUpdateResponse } from 'src/store/updateSiteCapacitySlice/types';
import { Region } from '../tasks/types';

class SitesService {
  /**
   * get all sites
   */
  public async getSites() {
    return await apiClient.get<Site[]>('/sites');
  }

  /**
   * get countries
   */
  public async getCountries() {
    return await apiClient.get<Country[]>('/countries');
  }

  /**
   * get regions
   * @returns {Object}
   */
  public async getRegions() {
    return await apiClient.get<Region[]>('/regions');
  }

  /**
   * get orgs
   */
  public async getOrgs() {
    return await apiClient.get<Org[]>('/organizations');
  }

  /**
   * get orgs
   */
  public async getSubOrgs() {
    return await apiClient.get<SubOrg[]>('/suborganizations');
  }

  /**
   * get buildingTypes
   */
  public async getBuildingTypes() {
    return await apiClient.get<BuildingType[]>('/buildingTypes');
  }

  /**
   * Create or update site
   */
  public async createOrUpdateSite(data: SitePayload, selectedSite?: TableDataRow) {
    let res = null;
    if (selectedSite) {
      res = await apiClient.put<Site>(`/sites/${selectedSite.id}`, data);
    } else {
      res = await apiClient.post<Site>('/sites', data);
    }
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: selectedSite ? 'Updated site successfully' : 'Created site successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * get applicable jobs for a site
   */
  public async getApplicableJobs(siteId: number) {
    return await apiClient.get<ApplicableJob[]>(`/sites/${siteId}/applicableJobs`);
  }

  /**
   * Create or update site capacity
   */
  public async createOrUpdateSiteCapacity(data: SiteCapacityPayload) {
    const res = await apiClient.post<SiteCapacityUpdateResponse>(`/sites/jobs`, data);

    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Updated site capacity successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * get region specific sites
   */
  public async getRegionSites(regionId: number) {
    return await apiClient.get<Site[]>(`/sites/region/${regionId}`);
  }

  /**
   * get all active sites
   */
  public async getActiveSites() {
    return await apiClient.get<Site[]>(`/sites/active`);
  }

  /**
   * get site by id
   */
  public async getSiteById(siteId: number) {
    return await apiClient.get<Site>(`/sites/${siteId}`);
  }
}

export default new SitesService();
